// Dynamic loan officer profile page, rendered at /<officer-slug> (e.g. /arizzi)
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import axios from "axios";
import {
  Phone,
  Mail,
  MessageSquare,
  ArrowRight,
  MapPin,
  ShieldCheck,
  Clock,
  Calculator,
  BadgeCheck,
  CheckCircle2,
  Send,
  Zap,
  FileText,
  Home,
  KeyRound,
  PartyPopper,
  Package,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import { getOfficerBySlug } from "@/data/loanOfficers";

const RED = "#D41F30";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HOUSE_BG = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='220' height='220' viewBox='0 0 220 220'%3E%3Cg fill='none' stroke='%23D41F30' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M30 95 L75 58 L120 95 M42 85 v46 h66 v-46 M63 131 v-24 h24 v24 M92 68 v-14 h10 v22'/%3E%3Cpath d='M140 178 L168 155 L196 178 M147 172 v28 h42 v-28 M160 200 v-14 h16 v14'/%3E%3C/g%3E%3C/svg%3E")`;

const HOMEOWNER_MOMENTS = [
  {
    img: "/img/yo93e753.webp",
    caption: "Offer accepted",
    icon: PartyPopper,
    tilt: "lg:-rotate-2",
  },
  {
    img: "/img/qqov8isa.webp",
    caption: "Keys in hand",
    icon: KeyRound,
    tilt: "lg:rotate-2",
  },
  {
    img: "/img/twsym6bn.webp",
    caption: "Move-in day",
    icon: Package,
    tilt: "lg:-rotate-1",
  },
  {
    img: "/img/25je1t4m.webp",
    caption: "Home at last",
    icon: Home,
    tilt: "lg:rotate-2",
  },
];

// ---------- scroll reveal ----------
const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setShown(true);
          obs.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : "translateY(28px)",
        transition: `opacity 0.7s ease ${delay}ms, transform 0.7s ease ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

// ---------- animated counter for calculator (Robinhood-style ticking numbers) ----------
const money = (v) => `$${Math.round(v).toLocaleString()}`;

const useTween = (value) => {
  const [display, setDisplay] = useState(value);
  const prev = useRef(value);
  useEffect(() => {
    const from = prev.current;
    const to = value;
    prev.current = value;
    if (from === to) return;
    const start = performance.now();
    const dur = 400;
    let raf;
    const tick = (t) => {
      const p = Math.min((t - start) / dur, 1);
      const e = 1 - Math.pow(1 - p, 3);
      setDisplay(from + (to - from) * e);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return display;
};

// ---------- mini mortgage calculator ----------
const MiniCalculator = ({ officer }) => {
  const [price, setPrice] = useState(425000);
  const [downPct, setDownPct] = useState(5);
  const [rate, setRate] = useState(6.5);
  const [term, setTerm] = useState(30);

  const { pi, taxes, ins, total, down } = useMemo(() => {
    const downAmt = price * (downPct / 100);
    const loan = price - downAmt;
    const r = rate / 100 / 12;
    const n = term * 12;
    const piVal = r > 0 ? (loan * r) / (1 - Math.pow(1 + r, -n)) : loan / n;
    const taxVal = (price * 0.011) / 12;
    const insVal = 105;
    return { pi: piVal, taxes: taxVal, ins: insVal, total: piVal + taxVal + insVal, down: downAmt };
  }, [price, downPct, rate, term]);

  const totalAnim = useTween(total);
  const piAnim = useTween(pi);
  const taxesAnim = useTween(taxes);

  const slider = (value, min, max, step, onChange, testid) => (
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      data-testid={testid}
      className="lo-slider w-full"
    />
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
      <div className="bg-white rounded-3xl border border-gray-100 shadow-xl p-8 space-y-7" data-testid="officer-calculator">
        <div>
          <div className="flex justify-between items-baseline mb-2">
            <label className="text-sm font-bold text-gray-900">Home price</label>
            <span className="font-display text-lg font-black" style={{ color: RED }}>{money(price)}</span>
          </div>
          {slider(price, 100000, 1500000, 5000, setPrice, "calc-price-slider")}
        </div>
        <div>
          <div className="flex justify-between items-baseline mb-2">
            <label className="text-sm font-bold text-gray-900">Down payment</label>
            <span className="font-display text-lg font-black" style={{ color: RED }}>
              {downPct}% <span className="text-gray-400 text-sm font-semibold">({money(down)})</span>
            </span>
          </div>
          {slider(downPct, 0, 30, 0.5, setDownPct, "calc-down-slider")}
        </div>
        <div>
          <div className="flex justify-between items-baseline mb-2">
            <label className="text-sm font-bold text-gray-900">Interest rate</label>
            <span className="font-display text-lg font-black" style={{ color: RED }}>{rate.toFixed(3)}%</span>
          </div>
          {slider(rate, 4, 9, 0.125, setRate, "calc-rate-slider")}
        </div>
        <div className="flex items-center gap-3">
          <label className="text-sm font-bold text-gray-900 mr-2">Loan term</label>
          {[30, 15].map((t) => (
            <button
              key={t}
              onClick={() => setTerm(t)}
              data-testid={`calc-term-${t}`}
              className={`px-5 py-2 rounded-full text-sm font-bold transition-all duration-300 ${
                term === t ? "text-white shadow-md scale-105" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
              }`}
              style={term === t ? { backgroundColor: RED } : {}}
            >
              {t} yr
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-3xl p-8 text-white flex flex-col justify-between shadow-xl" style={{ backgroundColor: "#111827" }}>
        <div>
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-gray-400 mb-3">Estimated monthly payment</p>
          <p className="font-display text-5xl md:text-6xl font-black leading-none mb-6 tabular-nums" data-testid="calc-monthly-total">
            {money(totalAnim)}
            <span className="text-xl text-gray-400 font-bold">/mo</span>
          </p>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between border-b border-white/10 pb-2">
              <span className="text-gray-400">Principal & interest</span>
              <span className="font-bold tabular-nums">{money(piAnim)}</span>
            </div>
            <div className="flex justify-between border-b border-white/10 pb-2">
              <span className="text-gray-400">Est. property taxes</span>
              <span className="font-bold tabular-nums">{money(taxesAnim)}</span>
            </div>
            <div className="flex justify-between border-b border-white/10 pb-2">
              <span className="text-gray-400">Est. home insurance</span>
              <span className="font-bold">{money(ins)}</span>
            </div>
          </div>
        </div>
        <div className="mt-8">
          <p className="text-xs text-gray-500 mb-4">
            Estimate only. {officer.firstName} will build your exact numbers, including programs that could lower this payment.
          </p>
          <a href="#contact-officer">
            <Button
              data-testid="calc-cta-btn"
              className="w-full bg-[#D41F30] hover:bg-[#B91C2A] text-white rounded-full py-6 text-base font-semibold shadow-lg transition-all duration-300 hover:scale-[1.02]"
            >
              Get my real numbers from {officer.firstName}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
};

// ---------- contact / apply form ----------
const OfficerContactForm = ({ officer }) => {
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "", timeline: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | done | error
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        leadType: "Officer Contact",
        officerName: officer.name,
        officerSlug: officer.slug,
        consent: true,
      });
      setStatus("done");
    } catch {
      setStatus("error");
    }
  };

  if (status === "done") {
    return (
      <div className="bg-white rounded-3xl shadow-xl p-10 text-center" data-testid="officer-form-success">
        <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-5">
          <CheckCircle2 className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="font-display text-3xl font-black text-gray-900 mb-3">You're on {officer.firstName}'s radar</h3>
        <p className="text-gray-600 mb-6 max-w-md mx-auto">
          {officer.firstName} personally reviews every request and will reach out shortly. Want an answer right now?
        </p>
        <a href={`tel:${officer.phoneDigits}`}>
          <Button className="bg-[#D41F30] hover:bg-[#B91C2A] text-white rounded-full px-8 py-6 text-base font-semibold" data-testid="success-call-btn">
            <Phone className="mr-2 w-5 h-5" /> Call {officer.firstName} at {officer.phone}
          </Button>
        </a>
      </div>
    );
  }

  const inputCls =
    "w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3.5 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#D41F30]/30 focus:border-[#D41F30] transition-all";

  return (
    <form onSubmit={submit} className="bg-white rounded-3xl shadow-xl p-8 md:p-10" data-testid="officer-contact-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <input required placeholder="First name" value={form.firstName} onChange={set("firstName")} className={inputCls} data-testid="officer-form-firstname" />
        <input required placeholder="Last name" value={form.lastName} onChange={set("lastName")} className={inputCls} data-testid="officer-form-lastname" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} className={inputCls} data-testid="officer-form-email" />
        <input required type="tel" placeholder="Phone" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="officer-form-phone" />
      </div>
      <select required value={form.timeline} onChange={set("timeline")} className={`${inputCls} mb-4 ${form.timeline ? "" : "text-gray-400"}`} data-testid="officer-form-timeline">
        <option value="" disabled>When are you looking to buy or refinance?</option>
        <option>ASAP, I'm ready now</option>
        <option>1-3 months</option>
        <option>3-6 months</option>
        <option>6+ months</option>
        <option>Just exploring my options</option>
      </select>
      <textarea
        rows={3}
        placeholder={`Anything ${officer.firstName} should know? (optional)`}
        value={form.message}
        onChange={set("message")}
        className={`${inputCls} mb-5 resize-none`}
        data-testid="officer-form-message"
      />
      <Button
        type="submit"
        disabled={status === "sending"}
        data-testid="officer-form-submit"
        className="w-full bg-[#D41F30] hover:bg-[#B91C2A] text-white rounded-full py-7 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.01]"
      >
        {status === "sending" ? (
          <>
            <Loader2 className="mr-2 w-5 h-5 animate-spin" /> Sending to {officer.firstName}...
          </>
        ) : (
          <>
            Apply Now <Send className="ml-2 w-5 h-5" />
          </>
        )}
      </Button>
      {status === "error" && (
        <p className="text-sm text-red-600 mt-3 text-center" data-testid="officer-form-error">
          Something went wrong. Please try again or call {officer.phone}.
        </p>
      )}
      <p className="text-[11px] text-gray-400 mt-4 leading-relaxed text-center">
        By submitting, you agree that {officer.name} and CityWorth Mortgage may contact you by phone, email, or text about your inquiry. No obligation, no impact to your credit.
      </p>
    </form>
  );
};

// ---------- page ----------
export default function LoanOfficerPage() {
  const { officerSlug } = useParams();
  const officer = getOfficerBySlug(officerSlug);

  if (!officer) return <Navigate to="/" replace />;

  const steps = [
    {
      icon: Send,
      title: "Reach out",
      text: `Call, text, or use the quick form below. ${officer.firstName} answers his phone, and actually likes it when it rings.`,
    },
    {
      icon: Zap,
      title: "Get your game plan",
      text: "A clear pre-approval with real numbers, program options, and ways to save homebuying dollars, all explained in plain English.",
    },
    {
      icon: Home,
      title: "Close on time",
      text: "Closing dates and time frames are professionally met by CityWorth's experienced operations team. No surprises.",
    },
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: officer.name,
    jobTitle: officer.title,
    telephone: officer.phone,
    email: officer.email,
    image: officer.photo,
    worksFor: { "@type": "Organization", name: "CityWorth Mortgage" },
    identifier: `NMLS ${officer.nmls}`,
  };

  return (
    <div className="bg-white" data-testid={`officer-page-${officer.slug}`}>
      <SEO
        title={`${officer.name} | ${officer.title} | CityWorth Mortgage`}
        description={`${officer.name}, ${officer.title} at CityWorth Mortgage (NMLS ${officer.nmls}). Licensed in ${officer.states.map((s) => s.code).join(", ")}. Fast answers, on-time closings. Call ${officer.phone}.`}
        path={`/${officer.slug}`}
        ogImage={officer.photo}
        jsonLd={jsonLd}
      />

      <style>{`
        @keyframes lo-fade-up { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes lo-float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        @keyframes lo-ping-slow { 0% { transform: scale(1); opacity: 0.6; } 100% { transform: scale(2.2); opacity: 0; } }
        .lo-hero-item { opacity: 0; animation: lo-fade-up 0.8s ease forwards; }
        .lo-float { animation: lo-float 5s ease-in-out infinite; }
        .lo-slider { -webkit-appearance: none; appearance: none; height: 6px; border-radius: 9999px; background: #f3f4f6; outline: none; }
        .lo-slider::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 22px; height: 22px; border-radius: 9999px; background: ${RED}; cursor: pointer; border: 3px solid #fff; box-shadow: 0 2px 8px rgba(212,31,48,0.4); transition: transform 0.15s ease; }
        .lo-slider::-webkit-slider-thumb:hover { transform: scale(1.2); }
        .lo-slider::-moz-range-thumb { width: 22px; height: 22px; border-radius: 9999px; background: ${RED}; cursor: pointer; border: 3px solid #fff; box-shadow: 0 2px 8px rgba(212,31,48,0.4); }
        .lo-state-badge { transition: transform 0.25s ease, box-shadow 0.25s ease, background-color 0.25s ease, color 0.25s ease; }
        .lo-state-badge:hover { transform: translateY(-4px) scale(1.06); background-color: ${RED}; color: #fff; box-shadow: 0 10px 24px rgba(212,31,48,0.35); }
        .lo-state-badge:hover .lo-state-name { color: rgba(255,255,255,0.85); }
      `}</style>

      {/* ============ HERO ============ */}
      <section className="relative pt-28 pb-16 md:pt-36 md:pb-24 overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-red-50 rounded-full blur-3xl opacity-60 -translate-y-1/3 translate-x-1/4 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gray-50 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Left */}
            <div className="lg:col-span-7">
              <div className="lo-hero-item inline-flex items-center gap-2 bg-red-50 rounded-full px-4 py-2 mb-6" style={{ animationDelay: "0ms" }}>
                <BadgeCheck className="w-4 h-4" style={{ color: RED }} />
                <span className="text-xs font-bold tracking-[0.18em] uppercase" style={{ color: RED }}>
                  CityWorth Loan Officer
                </span>
              </div>
              <h1 className="lo-hero-item font-display text-5xl md:text-6xl lg:text-7xl font-black text-gray-900 leading-[1.02] tracking-tight mb-4" style={{ animationDelay: "100ms" }}>
                {officer.firstName} <span style={{ color: RED }}>{officer.lastName}</span>
              </h1>
              <p className="lo-hero-item text-lg font-bold text-gray-900 mb-1" style={{ animationDelay: "180ms" }}>
                {officer.title} <span className="text-gray-400 font-semibold">| NMLS: {officer.nmls}</span>
              </p>
              <p className="lo-hero-item font-display text-2xl md:text-3xl font-bold text-gray-700 mb-8 italic" style={{ animationDelay: "240ms" }}>
                "{officer.tagline}"
              </p>

              <div className="lo-hero-item flex flex-col sm:flex-row gap-3 mb-8" style={{ animationDelay: "320ms" }}>
                <a href="#contact-officer">
                  <Button
                    data-testid="hero-apply-btn"
                    className="w-full sm:w-auto bg-[#D41F30] hover:bg-[#B91C2A] text-white rounded-full px-8 py-7 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                  >
                    Apply Now
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </a>
                <a href={`tel:${officer.phoneDigits}`}>
                  <Button
                    variant="outline"
                    data-testid="hero-call-btn"
                    className="w-full sm:w-auto rounded-full px-7 py-7 text-lg font-semibold border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white transition-all duration-300"
                  >
                    <Phone className="mr-2 w-5 h-5" /> {officer.phone}
                  </Button>
                </a>
              </div>

              <div className="lo-hero-item flex flex-wrap items-center gap-3 mb-8" style={{ animationDelay: "400ms" }}>
                <a
                  href={`sms:${officer.phoneDigits}`}
                  data-testid="hero-text-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-gray-100 hover:bg-gray-200 px-5 py-2.5 text-sm font-bold text-gray-700 transition-colors"
                >
                  <MessageSquare className="w-4 h-4" /> Text {officer.firstName}
                </a>
                <a
                  href={`mailto:${officer.email}`}
                  data-testid="hero-email-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-gray-100 hover:bg-gray-200 px-5 py-2.5 text-sm font-bold text-gray-700 transition-colors"
                >
                  <Mail className="w-4 h-4" /> {officer.email}
                </a>
              </div>

              <div className="lo-hero-item" style={{ animationDelay: "480ms" }}>
                <p className="text-xs font-bold tracking-[0.18em] uppercase text-gray-400 mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> Licensed in
                </p>
                <div className="flex flex-wrap gap-3" data-testid="officer-states">
                  {officer.states.map((s) => (
                    <div
                      key={s.code}
                      data-testid={`state-badge-${s.code}`}
                      className="lo-state-badge cursor-default bg-white border border-gray-200 rounded-2xl px-5 py-3 text-center shadow-sm"
                    >
                      <div className="font-display text-xl font-black leading-none">{s.code}</div>
                      <div className="lo-state-name text-[10px] font-bold text-gray-400 mt-1 uppercase tracking-wide">{s.name}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: photo */}
            <div className="lg:col-span-5 relative">
              <div className="lo-hero-item relative max-w-md mx-auto" style={{ animationDelay: "200ms" }}>
                <div className="absolute -inset-4 rounded-[2.5rem] bg-red-50 rotate-3" />
                <img
                  src={officer.photo}
                  alt={officer.name}
                  data-testid="officer-photo"
                  className="relative rounded-[2rem] w-full object-cover shadow-2xl"
                />
                <div className="lo-float absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl px-5 py-4 flex items-center gap-3">
                  <span className="relative flex h-3 w-3">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-green-500" style={{ animation: "lo-ping-slow 1.8s ease-out infinite" }} />
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500" />
                  </span>
                  <div>
                    <p className="text-sm font-black text-gray-900 leading-none">Taking new clients</p>
                    <p className="text-xs text-gray-400 mt-1">Calls & texts returned fast</p>
                  </div>
                </div>
                <div className="lo-float absolute -top-5 -right-4 bg-gray-900 text-white rounded-2xl shadow-xl px-5 py-3" style={{ animationDelay: "1.2s" }}>
                  <p className="font-display text-lg font-black leading-none">{officer.states.length} states</p>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wide mt-1">Licensed & ready</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ TRUST STRIP ============ */}
      <section className="border-y border-gray-100 bg-gray-50/60">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: Phone, label: "Always answers", sub: "Or promptly returns messages" },
            { icon: Clock, label: "On-time closings", sub: "Deadlines professionally met" },
            { icon: MapPin, label: `${officer.states.length} states licensed`, sub: officer.states.map((s) => s.code).join(" · ") },
            { icon: ShieldCheck, label: `NMLS ${officer.nmls}`, sub: "Fully licensed & vetted" },
          ].map((item, i) => (
            <Reveal key={item.label} delay={i * 100}>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-white shadow-sm border border-gray-100 flex items-center justify-center shrink-0">
                  <item.icon className="w-5 h-5" style={{ color: RED }} />
                </div>
                <div>
                  <p className="text-sm font-black text-gray-900 leading-tight">{item.label}</p>
                  <p className="text-xs text-gray-400">{item.sub}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============ BIO ============ */}
      <section className="py-20 md:py-28 bg-gray-900 text-white overflow-hidden relative">
        <div className="absolute top-0 left-1/2 w-[700px] h-[700px] bg-red-900/20 rounded-full blur-3xl -translate-y-1/2 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-xs font-bold tracking-[0.2em] uppercase mb-3 text-red-400">Meet your loan officer</p>
              <h2 className="font-display text-4xl md:text-5xl font-black tracking-tight mb-8">
                Hi, I'm {officer.name}...
              </h2>
            </Reveal>
            <div className="text-gray-300 leading-relaxed text-[17px]" data-testid="officer-bio">
              {officer.bio.map((p, i) => (
                <Reveal key={i} delay={i * 80}>
                  <p className={i < officer.bio.length - 1 ? "mb-8" : ""}>{p}</p>
                </Reveal>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 space-y-5 lg:pt-24">
            {officer.highlights.map((h, i) => (
              <Reveal key={h.label} delay={i * 150}>
                <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-colors duration-300">
                  <div className="flex items-start gap-4">
                    <CheckCircle2 className="w-6 h-6 shrink-0 mt-0.5" style={{ color: "#F87171" }} />
                    <div>
                      <p className="font-display text-xl font-black">{h.label}</p>
                      <p className="text-sm text-gray-400 mt-1">{h.detail}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
            <Reveal delay={450}>
              <a href="#contact-officer" className="block">
                <Button className="w-full bg-[#D41F30] hover:bg-[#B91C2A] text-white rounded-full py-7 text-lg font-semibold shadow-lg transition-all duration-300 hover:scale-[1.02]" data-testid="bio-cta-btn">
                  Work with {officer.firstName}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ HOW I WORK ============ */}
      <section className="py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: HOUSE_BG, backgroundSize: "220px 220px", opacity: 0.05 }} />
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-white via-white/40 to-white" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="text-center mb-14">
            <p className="text-xs font-bold tracking-[0.2em] uppercase mb-3" style={{ color: RED }}>How I work</p>
            <h2 className="font-display text-4xl md:text-5xl font-black text-gray-900 tracking-tight">
              Simple process. Elite technology.
            </h2>
            <p className="text-gray-500 mt-4 max-w-2xl mx-auto text-lg">
              Digital application, real-time updates, and one experienced human, {officer.firstName}, guiding every step.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-testid="how-i-work-steps">
            {steps.map((step, i) => (
              <Reveal key={step.title} delay={i * 150}>
                <div className="group relative bg-white border border-gray-100 rounded-3xl p-8 shadow-sm hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 h-full">
                  <div className="font-display text-7xl font-black text-gray-100 group-hover:text-red-50 transition-colors absolute top-4 right-6 select-none">
                    {i + 1}
                  </div>
                  <div className="relative w-14 h-14 rounded-2xl flex items-center justify-center mb-6" style={{ backgroundColor: "#FDF2F3" }}>
                    <step.icon className="w-7 h-7" style={{ color: RED }} />
                  </div>
                  <h3 className="relative font-display text-2xl font-black text-gray-900 mb-3">{step.title}</h3>
                  <p className="relative text-gray-500 leading-relaxed">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ HAPPY HOMEOWNERS ============ */}
      <section className="py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: HOUSE_BG, backgroundSize: "230px 230px", opacity: 0.06 }} />
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-white via-white/30 to-white" />
        <div className="absolute top-10 right-0 w-[420px] h-[420px] bg-red-50 rounded-full blur-3xl opacity-60 translate-x-1/3 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="text-center mb-14">
            <p className="text-xs font-bold tracking-[0.2em] uppercase mb-3" style={{ color: RED }}>The best part of the job</p>
            <h2 className="font-display text-4xl md:text-5xl font-black text-gray-900 tracking-tight">
              Every journey ends with keys.
            </h2>
            <p className="text-gray-500 mt-4 max-w-2xl mx-auto text-lg">
              Real moments from CityWorth homebuyers. Yours is next.
            </p>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-8" data-testid="happy-homeowners">
            {HOMEOWNER_MOMENTS.map((m, i) => (
              <Reveal key={m.caption} delay={i * 130}>
                <div
                  className={`group relative bg-white p-3 pb-4 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:scale-[1.05] hover:rotate-0 ${m.tilt}`}
                  data-testid={`homeowner-card-${i}`}
                >
                  <div className="overflow-hidden rounded-xl">
                    <img
                      src={m.img}
                      alt={m.caption}
                      loading="lazy"
                      className="w-full aspect-[4/5] object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="flex items-center gap-2 mt-3.5 px-1">
                    <span className="w-7 h-7 rounded-full bg-red-50 flex items-center justify-center shrink-0">
                      <m.icon className="w-3.5 h-3.5" style={{ color: RED }} />
                    </span>
                    <p className="text-sm font-black text-gray-900">{m.caption}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CALCULATOR ============ */}
      <section className="py-20 md:py-28 bg-gray-50/60">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-red-50 rounded-full px-4 py-2 mb-5">
              <Calculator className="w-4 h-4" style={{ color: RED }} />
              <span className="text-xs font-bold tracking-[0.18em] uppercase" style={{ color: RED }}>Instant estimate</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-black text-gray-900 tracking-tight">
              Play with the numbers
            </h2>
            <p className="text-gray-500 mt-4 max-w-2xl mx-auto text-lg">
              Drag the sliders for a live payment estimate, then let {officer.firstName} sharpen it with real rates and programs.
            </p>
          </Reveal>
          <Reveal delay={150}>
            <MiniCalculator officer={officer} />
          </Reveal>
        </div>
      </section>

      {/* ============ RESOURCES ============ */}
      <section className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="mb-10">
            <h2 className="font-display text-3xl md:text-4xl font-black text-gray-900 tracking-tight">
              Do your homework, I'll handle the hard part
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="officer-resources">
            {[
              { to: "/first-time-homebuyer-guide", icon: FileText, title: "First-Time Homebuyer Guide", text: "A 10-step walkthrough of the entire journey, start to keys-in-hand." },
              { to: "/loans/fha", icon: Home, title: "Explore loan programs", text: "FHA, VA, conventional, and more. See which fits your situation." },
              { to: "/mortgage-calculator", icon: Calculator, title: "Full mortgage calculator", text: "Dig deeper into payments, taxes, insurance, and PMI." },
            ].map((r, i) => (
              <Reveal key={r.to} delay={i * 120}>
                <Link
                  to={r.to}
                  data-testid={`resource-link-${i}`}
                  className="group block bg-white border border-gray-100 rounded-3xl p-7 shadow-sm hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300 h-full"
                >
                  <r.icon className="w-8 h-8 mb-5" style={{ color: RED }} />
                  <h3 className="font-display text-xl font-black text-gray-900 mb-2">{r.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed mb-4">{r.text}</p>
                  <span className="inline-flex items-center text-sm font-bold" style={{ color: RED }}>
                    Take a look
                    <ArrowRight className="ml-1.5 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CONTACT FORM ============ */}
      <section id="contact-officer" className="py-20 md:py-28 relative overflow-hidden scroll-mt-24">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-red-50 rounded-full blur-3xl opacity-60 -translate-y-1/2 -translate-x-1/3 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="text-xs font-bold tracking-[0.2em] uppercase mb-3" style={{ color: RED }}>Apply now</p>
              <h2 className="font-display text-4xl md:text-5xl font-black text-gray-900 tracking-tight mb-5">
                Let's get you home.
              </h2>
              <p className="text-lg text-gray-500 leading-relaxed mb-8">
                Sixty seconds is all it takes. Your request goes straight to {officer.firstName}, not a call center, and he'll reach out personally.
              </p>
              <div className="flex items-center gap-4">
                <img loading="lazy" src={officer.photo} alt={officer.name} className="w-16 h-16 rounded-2xl object-cover shadow-md" />
                <div>
                  <p className="font-black text-gray-900">{officer.name}</p>
                  <p className="text-sm text-gray-400">{officer.title} · NMLS {officer.nmls}</p>
                  <a href={`tel:${officer.phoneDigits}`} className="text-sm font-bold" style={{ color: RED }}>
                    {officer.phone}
                  </a>
                </div>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={150}>
              <OfficerContactForm officer={officer} />
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
